'use client';

import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCaretDown,
  faCheck,
  faSearch,
} from '@fortawesome/pro-solid-svg-icons';
import { useDebounce } from 'use-debounce';
import { CircularProgress } from 'src/components/PulseUI';
import { useClickOutside } from 'src/hooks/common/useClickOutside';

interface Strategy {
  id: number;
  label: string;
  description: string;
}

interface SearchableStrategySelectProps {
  value: number | null;
  onChange: (strategyId: number) => void;
  strategies: Strategy[];
  isLoading?: boolean;
  placeholder?: string;
  onSearchChange?: (search: string) => void;
  type: 'target' | 'engage';
}

const SearchableStrategySelect: React.FC<SearchableStrategySelectProps> = ({
  value,
  onChange,
  strategies,
  isLoading = false,
  placeholder = 'Select a strategy...',
  onSearchChange,
  type,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 300);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useClickOutside(dropdownRef, () => {
    setIsOpen(false);
    setSearch('');
  }, isOpen);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Notify parent of search changes
  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(debouncedSearch);
    }
  }, [debouncedSearch, onSearchChange]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearch('');
    }
  };

  const handleSelectStrategy = (strategy: Strategy) => {
    onChange(strategy.id);
    setIsOpen(false);
    setSearch('');
  };

  const selectedStrategy = strategies.find((s) => s.id === value);

  return (
    <div ref={dropdownRef} className="relative w-full">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleToggleDropdown}
        className="w-full px-4 py-2 bg-white border border-neutral-200 rounded-full hover:border-neutral-500 transition-all duration-200 flex items-center justify-between outline-none"
      >
        <span className="text-sm text-neutral-900 truncate">
          {selectedStrategy ? selectedStrategy.label : placeholder}
        </span>
        <Icon
          icon={faCaretDown}
          className={`w-3.5 h-3.5 text-neutral-500 flex-shrink-0 ml-2 transition-transform duration-200 ease-out ${
            isOpen ? '-rotate-90' : 'rotate-0'
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-50 max-h-80 flex flex-col">
          {/* Search Input */}
          <div className="p-3 border-b border-neutral-200">
            <div className="relative">
              <Icon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-500"
              />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${type} strategies...`}
                className="w-full pl-9 pr-3 py-2 text-sm border border-neutral-200 rounded-full outline-none"
              />
            </div>
          </div>

          {/* Strategy List */}
          <div className="overflow-y-auto flex-1">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <CircularProgress size="sm" color="neutral" />
              </div>
            ) : strategies.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-neutral-600">
                No strategies found
              </div>
            ) : (
              strategies.map((strategy) => {
                const isSelected = value === strategy.id;
                return (
                  <button
                    key={strategy.id}
                    type="button"
                    onClick={() => handleSelectStrategy(strategy)}
                    className="w-full px-4 py-3 text-left hover:bg-neutral-50 transition-colors duration-200 flex items-start gap-3 border-b border-neutral-100 last:border-b-0"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-neutral-900 mb-1">
                        {strategy.label}
                      </div>
                      {strategy.description && (
                        <div className="text-xs text-neutral-600 line-clamp-2">
                          {strategy.description}
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <Icon
                        icon={faCheck}
                        className="w-4 h-4 text-sky-700 flex-shrink-0 mt-0.5"
                      />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableStrategySelect;
